/* src/components/features/journey-planning/Form/TravelPreferences/TravelPreferences.tsx */
import type { JSX } from 'preact';
import { Heading } from '../../../../base/Heading/Heading';
import { SvgIcon } from '../../../../base/Icon/SvgIcon';
import { SvgName } from '../../../../base/Icon/SvgRegistry';
import { titleCase } from '../../formatters';
import {
  type JourneyPlanAdvancedFilters,
  type JourneyPlanTravelOption,
} from '../../../../../data/journeyPlanSearch';
import styles from './TravelPreferences.module.css';

export interface TransportOption {
  id: JourneyPlanTravelOption;
  label: string;
  icon: SvgName;
}

export type TravelFilters = JourneyPlanAdvancedFilters;

interface TravelPreferencesProps {
  filters: TravelFilters;
  setFilters: (filters: TravelFilters) => void;
}

interface SelectableOptionRowProps {
  checked: boolean;
  label: string;
  icon?: SvgName;
  inputId?: string;
  onChange: JSX.GenericEventHandler<HTMLInputElement>;
}

function clickOnEnter(e: JSX.TargetedKeyboardEvent<HTMLInputElement>) {
  if (e.key !== 'Enter') return;
  e.preventDefault();
  (e.currentTarget as HTMLInputElement).click();
}

const transportOptions: TransportOption[] = [
  { id: 'bus', label: 'Bus', icon: 'bus' },
  { id: 'ferry', label: 'Ferry', icon: 'ferry' },
  { id: 'train', label: 'Train', icon: 'train' },
  { id: 'coach', label: 'Coach', icon: 'coach' },
  { id: 'tram', label: 'Tram', icon: 'tram' },
  { id: 'tube', label: 'Tube', icon: 'tube' },
];

const walkIconMap: Record<'slow' | 'medium' | 'fast', SvgName> = {
  slow: 'walkSlow',
  medium: 'walkMedium',
  fast: 'walkFast',
};

type Speed = keyof typeof walkIconMap;
const speedOptions = Object.keys(walkIconMap) as Speed[];

function joinClassNames(...classNames: Array<string | false | undefined>) {
  return classNames.filter(Boolean).join(' ');
}

function SelectableOptionRow({
  checked,
  label,
  icon,
  inputId,
  onChange,
}: SelectableOptionRowProps) {
  return (
    <label
      htmlFor={inputId}
      className={`${styles.optionRow} ${checked ? styles.optionSelected : ''}`}
    >
      <input
        id={inputId}
        type="checkbox"
        className={styles.hiddenInput}
        checked={checked}
        onChange={onChange}
        onKeyDown={clickOnEnter}
      />
      <div className={styles.optionContent}>
        {icon ? (
          <SvgIcon name={icon} size={24} className={styles.optionIcon} />
        ) : null}
        <span className={styles.label}>{label}</span>
      </div>
      <div className={styles.checkbox}>
        {checked ? <span className={styles.checkboxMark}>✓</span> : null}
      </div>
    </label>
  );
}

export function TravelPreferences({
  filters,
  setFilters,
}: TravelPreferencesProps) {
  const { travel, minimalWalking, walkingSpeed } = filters;

  const toggleTravel = (transport: JourneyPlanTravelOption) => {
    const nextTravel = travel.includes(transport)
      ? travel.filter((value) => value !== transport)
      : [...travel, transport];

    setFilters({ ...filters, travel: nextTravel });
  };

  return (
    <div className={styles.container}>
      <fieldset id="travelBy" className={styles.section}>
        <legend className={styles.sectionTitleContainer}>
          <Heading as="h4" variant="sm">
            Travel by...
          </Heading>
        </legend>

        <div className={styles.transportList}>
          {transportOptions.map((option) => (
            <SelectableOptionRow
              key={option.id}
              inputId={`travel-by-${option.id}`}
              checked={travel.includes(option.id)}
              label={option.label}
              icon={option.icon}
              onChange={() => toggleTravel(option.id)}
            />
          ))}
        </div>
      </fieldset>

      <fieldset
        id="walkingSpeed"
        className={joinClassNames(styles.section, styles.sectionSpacious)}
      >
        <legend className={styles.sectionTitleContainer}>
          <Heading as="h3" variant="sm">
            Walking speed
          </Heading>
        </legend>

        <div
          className={styles.speedGrid}
          role="radiogroup"
          aria-label="Walking speed"
        >
          {/* TODO: I am not a huge fan of writing manual accessibility code like this. */}
          {speedOptions.map((speed) => (
            <button
              key={speed}
              type="button"
              role="radio"
              aria-checked={walkingSpeed === speed}
              tabIndex={0}
              className={joinClassNames(
                styles.speedRadioBtn,
                walkingSpeed === speed && styles.speedSelected,
              )}
              onClick={() => setFilters({ ...filters, walkingSpeed: speed })}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  setFilters({ ...filters, walkingSpeed: speed });
                  return;
                }

                if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
                e.preventDefault();

                const currentIndex = speedOptions.indexOf(speed);
                const nextIndex =
                  e.key === 'ArrowRight'
                    ? (currentIndex + 1) % speedOptions.length
                    : (currentIndex - 1 + speedOptions.length) %
                      speedOptions.length;
                const nextSpeed = speedOptions[nextIndex];
                setFilters({ ...filters, walkingSpeed: nextSpeed });

                const buttons =
                  e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
                    'button[role="radio"]',
                  ) ?? null;
                buttons?.[nextIndex]?.focus();
              }}
            >
              <span className={styles.speedOptionContent}>
                <SvgIcon
                  size={40}
                  className={styles.optionIcon}
                  name={walkIconMap[speed]}
                />
                {titleCase(speed)}
              </span>
            </button>
          ))}
        </div>
      </fieldset>

      <div id="walkingPreferences" className={styles.walkingPreferencesSection}>
        <SelectableOptionRow
          inputId="minimal-walking-checkbox"
          checked={minimalWalking}
          label="Minimal walking"
          onChange={(e) =>
            setFilters({
              ...filters,
              minimalWalking: e.currentTarget.checked,
            })
          }
        />
      </div>
    </div>
  );
}
